import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import InlineError from 'components/UI/InlineError';
import useLiterals from 'utils/hooks/useLiterals';
import { fetchNotificationDetail } from 'modules/notifications/actions';
import { NOTIFICATION_CATEGORIES } from 'modules/notifications/categories';

const NotificationDetail = ({ id }) => {
  const literals = useLiterals('notifications');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setDetail(null);
    fetchNotificationDetail(id)
      .then((response) => {
        if (active) setDetail(response);
      })
      .catch((err) => {
        if (!active || err?.statusCode === 401) return;
        if (err?.statusCode === 404) {
          setError(literals.unavailable);
          return;
        }
        if (err?.statusCode === 403) {
          setError(literals.forbidden);
          return;
        }
        setError(literals.loadError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, literals.unavailable, literals.forbidden, literals.loadError]);

  if (loading) {
    return <p className='text-neutral-500'>{literals.loading}</p>;
  }

  if (error) {
    return <InlineError error={error} />;
  }

  const categoryKey = NOTIFICATION_CATEGORIES
    .find((item) => item.value === detail?.category)?.literal;
  const categoryLabel = categoryKey ? literals[categoryKey] : '';

  return (
    <article>
      {detail?.dateLabel ? (
        <p className='text-sm text-neutral-500 mb-2'>{detail.dateLabel}</p>
      ) : null}
      <h2 className='mb-2'>{detail?.title}</h2>
      {categoryLabel ? (
        <p className='text-sm text-neutral-500 mb-4'>{categoryLabel}</p>
      ) : null}
      <p className='whitespace-pre-wrap break-words'>{detail?.content}</p>
    </article>
  );
};

NotificationDetail.propTypes = {
  id: PropTypes.string.isRequired,
};

export default NotificationDetail;
